import { Button, type ButtonProps } from '@/shared/components/ui/button';
import type { AppOnlyFeatureKey } from '@/shared/constants/appOnlyFeatures';
import { cn } from '@/shared/lib/utils';
import { useUIStore } from '@/stores/uiStore';

/**
 * 거래처 화면의 앱 전용 버튼 — v1.0 디자인은 [앱] 배지 없이 비활성 모양 버튼만 둔다
 * (거래 정보 [필터링 신청] · [추가 필터링] 833:8137 Outline, 드로어 행 [필터링 신청] 842:7308 Filled/grey Small).
 * 보기에는 비활성이지만 클릭은 받아서 X6 공통 앱 안내 팝업을 연다 (disabled 대신 aria-disabled).
 * 공통 AppOnlyButton(레거시 모양 + [앱] 배지)을 바꾸지 않고 여기서 Button으로 조립한다.
 */
export const AppOnlyActionButton = ({
  feature,
  variant = 'outline',
  size = 'medium',
  className,
  children,
}: {
  feature: AppOnlyFeatureKey;
  variant?: Extract<ButtonProps['variant'], 'outline' | 'grey'>;
  size?: Extract<ButtonProps['size'], 'medium' | 'small'>;
  className?: string;
  children: string;
}) => {
  const openAppOnly = useUIStore((s) => s.openAppOnly);
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      aria-disabled="true"
      onClick={(e) => {
        // 드로어 행 안에서 눌러도 행 클릭(M-12 열기)으로 번지지 않게 한다
        e.stopPropagation();
        openAppOnly(feature);
      }}
      className={cn(
        // aria-disabled는 공통 스타일에서 pointer-events를 막으므로 다시 연다
        'pointer-events-auto cursor-default text-fg-disabled hover:bg-surface-white active:bg-surface-white aria-disabled:pointer-events-auto',
        variant === 'grey' && 'bg-grey-50 hover:bg-grey-50 active:bg-grey-50',
        className,
      )}
    >
      {children}
    </Button>
  );
};
